import React, { useState } from 'react';
interface SliderInputProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit?: string;
  onChange: (value: number) => void;
  icon?: React.ReactNode;
  formatValue?: (value: number) => string;
}
/**
 * SliderInput component
 * Interactive slider with label, value display, and icon
 * Enhanced with smooth animations and better visual feedback
 * Fixed alignment issues for pixel-perfect design
 */
export function SliderInput({
  label,
  value,
  min,
  max,
  step,
  unit = '',
  onChange,
  icon,
  formatValue
}: SliderInputProps) {
  const [isDragging, setIsDragging] = useState(false);
  const percentage = (value - min) / (max - min) * 100;
  const displayValue = formatValue ? formatValue(value) : `${value}${unit}`;
  return <div className="space-y-4" data-id="element-2140">
      {/* Label and Value - Fixed Alignment */}
      <div className="flex items-center justify-between" data-id="element-2141">
        <div className="flex items-center gap-2.5" data-id="element-2142">
          {icon && <div className={`flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-300 ${isDragging ? 'bg-[#3ab795]/20 text-[#3ab795] scale-110' : 'bg-[#3ab795]/10 text-[#3ab795]'}`} data-id="element-2143">
              {icon}
            </div>}
          <label className="text-sm font-medium text-white/90 leading-none" data-id="element-2144">
            {label}
          </label>
        </div>
        <div className="flex items-baseline gap-1" data-id="element-2145">
          <span className={`text-2xl font-bold transition-all duration-300 leading-none ${isDragging ? 'text-[#3ab795] scale-110' : 'text-[#3ab795]'}`} data-id="element-2146">
            {displayValue}
          </span>
        </div>
      </div>

      {/* Slider Container - Improved Vertical Alignment */}
      <div className="relative flex items-center h-2" data-id="element-2147">
        {/* Track Background with Glow */}
        <div className="absolute inset-0 h-2 rounded-full bg-white/5 overflow-hidden" data-id="element-2148">
          {/* Animated glow effect */}
          <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-[#3ab795]/20 to-transparent transition-opacity duration-300 ${isDragging ? 'opacity-100' : 'opacity-0'}`} style={{
          transform: `translateX(${percentage - 50}%)`
        }} data-id="element-2149" />
        </div>

        {/* Progress Fill */}
        <div className="absolute top-0 left-0 h-2 rounded-full bg-gradient-to-r from-[#3ab795] to-[#2d9f7e] transition-all duration-150" style={{
        width: `${percentage}%`
      }} data-id="element-2150">
          {/* Shimmer effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" data-id="element-2151" />
        </div>

        {/* Slider Input */}
        <input type="range" min={min} max={max} step={step} value={value} onChange={e => onChange(Number(e.target.value))} onMouseDown={() => setIsDragging(true)} onMouseUp={() => setIsDragging(false)} onTouchStart={() => setIsDragging(true)} onTouchEnd={() => setIsDragging(false)} className="relative w-full h-2 appearance-none cursor-pointer bg-transparent z-10" style={{
        WebkitAppearance: 'none'
      }} data-id="element-2152" />

        {/* Custom Thumb (styled via CSS) */}
        <style jsx data-id="element-2153">{`
          input[type='range']::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: linear-gradient(135deg, #3ab795 0%, #2d9f7e 100%);
            cursor: pointer;
            border: 3px solid #18181b;
            box-shadow:
              0 2px 8px rgba(58, 183, 149, 0.4),
              0 0 0 0 rgba(58, 183, 149, 0.4);
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          }

          input[type='range']::-webkit-slider-thumb:hover {
            transform: scale(1.15);
            box-shadow:
              0 4px 12px rgba(58, 183, 149, 0.6),
              0 0 0 4px rgba(58, 183, 149, 0.1);
          }

          input[type='range']::-webkit-slider-thumb:active {
            transform: scale(1.25);
            box-shadow:
              0 4px 16px rgba(58, 183, 149, 0.8),
              0 0 0 6px rgba(58, 183, 149, 0.15);
          }

          input[type='range']::-moz-range-thumb {
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: linear-gradient(135deg, #3ab795 0%, #2d9f7e 100%);
            cursor: pointer;
            border: 3px solid #18181b;
            box-shadow:
              0 2px 8px rgba(58, 183, 149, 0.4),
              0 0 0 0 rgba(58, 183, 149, 0.4);
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          }

          input[type='range']::-moz-range-thumb:hover {
            transform: scale(1.15);
            box-shadow:
              0 4px 12px rgba(58, 183, 149, 0.6),
              0 0 0 4px rgba(58, 183, 149, 0.1);
          }

          input[type='range']::-moz-range-thumb:active {
            transform: scale(1.25);
            box-shadow:
              0 4px 16px rgba(58, 183, 149, 0.8),
              0 0 0 6px rgba(58, 183, 149, 0.15);
          }

          input[type='range']:focus {
            outline: none;
          }

          input[type='range']:focus-visible::-webkit-slider-thumb {
            box-shadow:
              0 4px 12px rgba(58, 183, 149, 0.6),
              0 0 0 4px rgba(58, 183, 149, 0.2);
          }

          input[type='range']:focus-visible::-moz-range-thumb {
            box-shadow:
              0 4px 12px rgba(58, 183, 149, 0.6),
              0 0 0 4px rgba(58, 183, 149, 0.2);
          }

          @keyframes shimmer {
            0% {
              transform: translateX(-100%);
            }
            100% {
              transform: translateX(100%);
            }
          }

          .animate-shimmer {
            animation: shimmer 2s infinite;
          }
        `}</style>
      </div>

      {/* Min/Max Labels with improved spacing and alignment */}
      <div className="flex justify-between items-center mt-2.5" data-id="element-2154">
        <span className="text-xs font-medium text-[#b8bcc8]/60 leading-none" data-id="element-2155">
          {formatValue ? formatValue(min) : `${min}${unit}`}
        </span>
        <span className="text-xs font-medium text-[#b8bcc8]/60 leading-none" data-id="element-2156">
          {formatValue ? formatValue(max) : `${max}${unit}`}
        </span>
      </div>
    </div>;
}